import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Copy, Check, Loader2 } from 'lucide-react';
import CategoryBadge from './CategoryBadge';
import { useBroadcast } from '../hooks/useBroadcast';
import { ApiError } from '../lib/api';
import { LANGS, type FeedItemDto, type LangCode, type BroadcastDto } from '@shared/types';

interface Props {
  item: FeedItemDto;
  // Selection (pro newsletter builder v archivu):
  selectable?: boolean;
  selected?: boolean;
  selectDisabled?: boolean;
  onToggleSelected?: () => void;
}

export default function FeedCard({
  item,
  selectable = false,
  selected = false,
  selectDisabled = false,
  onToggleSelected,
}: Props) {
  const [openLang, setOpenLang] = useState<LangCode | null>(null);
  const broadcastQuery = useBroadcast(item.id, openLang !== null);

  return (
    <article className={`card overflow-hidden ${selected ? 'ring-2 ring-teal' : ''}`}>
      <div className="p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {selectable && (
              <input
                type="checkbox"
                className="h-5 w-5 cursor-pointer accent-teal disabled:cursor-not-allowed disabled:opacity-30"
                checked={selected}
                disabled={selectDisabled && !selected}
                onChange={() => onToggleSelected?.()}
                title={selectDisabled && !selected ? 'Max 5 článků' : 'Vybrat pro newsletter'}
              />
            )}
            <CategoryBadge category={item.category} />
          </div>
          <span className="text-xs text-gray-500">
            {formatDate(item.publishedAt ?? item.pickedAt)}
          </span>
        </div>

        <h2 className="mb-2 font-heading text-xl font-semibold leading-snug text-dark">
          {item.titleCz}
        </h2>

        <p className="mb-4 text-sm leading-relaxed text-gray-700">{item.tldrCz}</p>

        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
          <span>📰</span>
          <a
            href={item.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-teal hover:underline"
          >
            {item.sourceName}
          </a>
          <ExternalLink className="h-3 w-3" />
        </div>
      </div>

      <LangTabStrip
        openLang={openLang}
        broadcast={broadcastQuery.data ?? null}
        onSelect={(lang) => setOpenLang(openLang === lang ? null : lang)}
      />

      {openLang && (
        <BroadcastPanel
          lang={openLang}
          loading={broadcastQuery.isLoading}
          error={broadcastQuery.error as Error | null}
          broadcast={broadcastQuery.data ?? null}
        />
      )}
    </article>
  );
}

function LangTabStrip({
  openLang,
  broadcast,
  onSelect,
}: {
  openLang: LangCode | null;
  broadcast: BroadcastDto | null;
  onSelect: (lang: LangCode) => void;
}) {
  // Status na záložce: ✓ (máme), … (necháváme generovat), nic (zatím se nepokoušelo)
  const isAvailable = (lang: LangCode): boolean => {
    if (!broadcast) return false;
    const key = `body${lang.charAt(0).toUpperCase() + lang.slice(1)}` as keyof BroadcastDto;
    const v = broadcast[key];
    return typeof v === 'string' && v.length > 0;
  };

  return (
    <div className="flex border-t border-gray-100 bg-gray-50 text-sm">
      {LANGS.map((l) => {
        const active = openLang === l.code;
        const hasContent = isAvailable(l.code);
        return (
          <button
            key={l.code}
            onClick={() => onSelect(l.code)}
            className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-3 font-medium transition-colors border-r border-gray-200 last:border-r-0 ${
              active ? 'bg-white text-teal' : 'text-gray-600 hover:bg-teal-light hover:text-teal'
            }`}
            title={`Vzor zprávy ${l.label}`}
          >
            <span aria-hidden>{l.flag}</span>
            <span className="hidden sm:inline">{l.label}</span>
            <span className="inline sm:hidden text-xs uppercase">{l.code}</span>
            {hasContent && <span className="text-positive text-xs">●</span>}
          </button>
        );
      })}
    </div>
  );
}

function BroadcastPanel({
  lang,
  loading,
  error,
  broadcast,
}: {
  lang: LangCode;
  loading: boolean;
  error: Error | null;
  broadcast: BroadcastDto | null;
}) {
  const [copied, setCopied] = useState(false);

  const body = broadcast
    ? lang === 'cs'
      ? broadcast.bodyCs
      : lang === 'en'
        ? broadcast.bodyEn
        : lang === 'pl'
          ? broadcast.bodyPl
          : broadcast.bodyVi
    : null;

  const onCopy = async () => {
    if (!body) return;
    await navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 border-t border-gray-200 bg-white p-5 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Generuju broadcast (Sonnet, ~5–15s)…
      </div>
    );
  }

  if (error) {
    if (error instanceof ApiError && error.code === 'PROFILE_INCOMPLETE') {
      return (
        <div className="border-t border-gray-200 bg-white p-5">
          <div className="rounded-md border border-warning/40 bg-warning-bg p-4 text-sm text-warning">
            <p className="mb-2 font-semibold">Chybí podpis v profilu</p>
            <p className="mb-3">{error.message}</p>
            <Link to="/profile" className="btn-secondary text-xs">
              Otevřít profil →
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="border-t border-gray-200 bg-white p-5">
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          Chyba generování: {error.message}
        </div>
      </div>
    );
  }

  if (!body) {
    const langLabel = LANGS.find((l) => l.code === lang)?.label ?? lang;
    return (
      <div className="border-t border-gray-200 bg-white p-5">
        <div className="rounded-md border border-warning/40 bg-warning-bg p-4 text-sm text-warning">
          <p className="mb-2 font-semibold">Chybí {langLabel} podpis</p>
          <p className="mb-3">
            Doplň podpis v {langLabel} v profilu — broadcast v tomhle jazyce se pak
            vygeneruje při dalším otevření karty.
          </p>
          <Link to="/profile" className="btn-secondary text-xs">
            Otevřít profil →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 bg-white p-5">
      <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-gray-700">
        {body}
      </pre>
      <div className="mt-4 flex justify-end">
        <button onClick={onCopy} className="btn-primary text-xs">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Zkopírováno do schránky' : 'Kopírovat do schránky'}
        </button>
      </div>
    </div>
  );
}

function formatDate(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const today = new Date();
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (isToday) {
    return `Dnes ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  const days = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
  return `${days[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;
}
